import { useEffect, useState } from 'react';
import { useToast } from '../hooks/useToast.tsx';
import { Database, Table2, Search, Play, RefreshCw } from 'lucide-react';
import { adminDbApi } from '../services/api';
import type { DbInfo, TableData } from '../services/api';

export function AdminDbPage() {
  const { success, error } = useToast();
  const [info, setInfo] = useState<DbInfo | null>(null);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [page, setPage] = useState(1);
  const [sql, setSql] = useState('SELECT * FROM utilizadores LIMIT 20;');
  const [queryResult, setQueryResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const loadInfo = async () => {
    try {
      const j = await adminDbApi.info();
      if (j.success && j.data) {
        setInfo(j.data);
        if (!selectedTable && j.data.tables[0]) setSelectedTable(j.data.tables[0]);
      }
    } catch {
      error('Erro ao carregar info da BD');
    }
  };

  const loadTable = async (table: string, p = 1) => {
    setLoading(true);
    try {
      const res = await adminDbApi.table(table, p, 50);
      if (res.success && res.data) { setTableData(res.data); setPage(p); }
      else error(res.message || 'Erro ao carregar tabela');
    } catch {
      error('Erro ao carregar tabela');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadInfo(); }, []);
  useEffect(() => { if (selectedTable) loadTable(selectedTable, 1); }, [selectedTable]);

  const runQuery = async () => {
    try {
      const j = await adminDbApi.rawQuery(sql);
      if (j.success) { setQueryResult(j.data); success('Query executada'); loadInfo(); }
      else error(j.message || 'Erro na query');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro na query';
      // backend agora só permite SELECT/WITH — mensagem já vem clara
      error(msg);
    }
  };

  return (
    <div className="page-container" style={{ maxWidth: 1400 }}>
      <div className="page-header">
        <h1 className="page-title">Gestão BD <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-muted)' }}>(somente leitura)</span></h1>
        <div className="header-actions">
          <button className="btn btn-ghost btn-sm" onClick={loadInfo}><RefreshCw className="w-4 h-4" />Atualizar</button>
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{info ? `${(info.size / 1024).toFixed(1)} KB • ${info.tables.length} tabelas` : ''}</span>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}><Database className="w-5 h-5" />Tabelas</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {info?.tables.map(t => (
            <button key={t} onClick={() => setSelectedTable(t)} className={`btn btn-sm ${selectedTable === t ? 'btn-primary' : 'btn-secondary'}`}>
              <Table2 className="w-4 h-4" />{t} <span style={{ opacity: 0.7, fontSize: 11 }}>({info.counts[t]})</span>
            </button>
          ))}
        </div>
        {loading && <p style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>A carregar...</p>}
        {tableData && !loading && (
          <>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 8 }}>{tableData.total} linhas • pág {tableData.page} • edição via páginas próprias (Clientes, Mesas, etc.)</div>
            <div className="table-container" style={{ maxHeight: 420, overflow: 'auto' }}>
              <table className="table">
                <thead><tr>{tableData.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {tableData.rows.map((row, idx: number) => (
                    <tr key={String((row as Record<string, unknown>)?.id ?? (row as Record<string, unknown>)?.Id ?? idx) + idx}>
                      {tableData.columns.map(col => (
                        <td key={col} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String((row as Record<string, unknown>)?.[col] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => loadTable(selectedTable, page - 1)}>Anterior</button>
              <button className="btn btn-secondary btn-sm" disabled={page * 50 >= tableData.total} onClick={() => loadTable(selectedTable, page + 1)}>Seguinte</button>
            </div>
          </>
        )}
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}><Search className="w-5 h-5" />SQL Console <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>— apenas SELECT/WITH</span></h3>
        <textarea className="input" rows={4} value={sql} onChange={e => setSql(e.target.value)} placeholder="SELECT * FROM ..." style={{ fontFamily: 'monospace', fontSize: 13 }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={runQuery}><Play className="w-4 h-4" />Executar</button>
        </div>
        {queryResult != null && <pre style={{ marginTop: 12, background: 'var(--c-surface-3)', padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 300, fontSize: 12 }}>{JSON.stringify(queryResult, null, 2)}</pre>}
      </div>
    </div>
  );
}
